import { useEffect, useRef, useState } from "react";

export function useDirtySyncedState<T>({
  value,
  resetKey,
  isEqual = Object.is,
}: {
  value: T;
  resetKey: string;
  isEqual?: (a: T, b: T) => boolean;
}) {
  const [draft, setDraft] = useState(value);
  const [baseline, setBaseline] = useState(value);
  const resetKeyRef = useRef(resetKey);
  const dirty = !isEqual(draft, baseline);

  useEffect(() => {
    const resetChanged = resetKeyRef.current !== resetKey;
    if (resetChanged) {
      resetKeyRef.current = resetKey;
      setBaseline(value);
      setDraft(value);
      return;
    }

    setBaseline((current) => {
      if (isEqual(current, value)) return current;
      if (!dirty) setDraft(value);
      return value;
    });
  }, [dirty, isEqual, resetKey, value]);

  return {
    draft,
    setDraft,
    dirty,
    baseline,
    resetToBaseline: () => setDraft(baseline),
    commitBaseline: (next: T) => {
      setBaseline(next);
      setDraft(next);
    },
  };
}
